import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/app/Components/AuthProvider';
import { supabase } from '@/app/lib/supabase';

export function useOnboardingStatus() {
  const { user } = useAuth();
  const [hasCompletedOnboarding, setHasCompletedOnboarding] = useState<boolean | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [statusOwnerId, setStatusOwnerId] = useState<string | null>(null);
  const ownerRef = useRef<string | null>(user?.id ?? null);
  const readGenerationRef = useRef(0);
  ownerRef.current = user?.id ?? null;

  useEffect(() => {
    if (!user) {
      readGenerationRef.current += 1;
      setStatusOwnerId(null);
      setHasCompletedOnboarding(null);
      setIsLoading(false);
      return;
    }

    void checkOnboardingStatus();
  }, [user?.id]);

  const checkOnboardingStatus = async () => {
    const requestedOwnerId = user?.id;
    if (!requestedOwnerId) return;
    const generation = ++readGenerationRef.current;
    try {
      setStatusOwnerId(requestedOwnerId);
      setIsLoading(true);
      setError(null);
      const { data, error: funcError } = await supabase.functions.invoke('onboarding', {
        body: { action: 'get' }
      });

      if (funcError) {
        throw funcError;
      }
      const completed = !!(data && data.completed_at);
      if (ownerRef.current !== requestedOwnerId || readGenerationRef.current !== generation) return;
      setHasCompletedOnboarding(completed);
    } catch (err) {
      if (ownerRef.current !== requestedOwnerId || readGenerationRef.current !== generation) return;
      console.error('Error checking onboarding status');
      setError(err);
      setHasCompletedOnboarding(null);
    } finally {
      if (ownerRef.current === requestedOwnerId && readGenerationRef.current === generation) setIsLoading(false);
    }
  };

  const markOnboardingComplete = async (onboardingData: any) => {
    const requestedOwnerId = user?.id;
    if (!requestedOwnerId) throw new Error('Sign in before saving onboarding.');
    try {
      setIsLoading(true);
      setError(null); // Clear errors
      const { data, error } = await supabase.functions.invoke('onboarding', {
        body: { 
          action: 'create',
          ...onboardingData
        }
      });

      if (error) {
        throw error;
      }

      if (ownerRef.current === requestedOwnerId) setHasCompletedOnboarding(true);
      return data;
    } catch (err) {
      console.error('Error marking onboarding complete');
      if (ownerRef.current === requestedOwnerId) setError(err);
      throw err;
    } finally {
      if (ownerRef.current === requestedOwnerId) setIsLoading(false);
    }
  };

  const skipOnboarding = async () => {
    const requestedOwnerId = user?.id;
    if (!requestedOwnerId) throw new Error('Sign in before skipping onboarding.');
    try {
      setIsLoading(true);
      setError(null); // Clear errors
      const { data, error } = await supabase.functions.invoke('onboarding', {
        body: { action: 'skip' }
      });

      if (error) {
        throw error;
      }

      if (ownerRef.current === requestedOwnerId) setHasCompletedOnboarding(true);
      return data;
    } catch (err) {
      console.error('Error skipping onboarding');
      if (ownerRef.current === requestedOwnerId) setError(err);
      throw err;
    } finally {
      if (ownerRef.current === requestedOwnerId) setIsLoading(false);
    }
  };

  const statusBelongsToCurrentUser = statusOwnerId === (user?.id ?? null);
  return {
    hasCompletedOnboarding: statusBelongsToCurrentUser ? hasCompletedOnboarding : null,
    isLoading: statusBelongsToCurrentUser ? isLoading : true,
    error: statusBelongsToCurrentUser ? error : null,
    checkOnboardingStatus,
    markOnboardingComplete,
    skipOnboarding
  };
}
